/**
 * AcceptInvitePage — iter149.
 *
 * Landing page for the one-click "Accept invite" button in the team-invite
 * email. Handles the three states cleanly:
 *
 *   • Not logged in       → prompt to register (with email pre-filled) or log in,
 *                            then auto-accept the invite on return.
 *   • Logged in, correct email → one-click POST /api/teams/join?token=...
 *   • Logged in, wrong email   → tell them to switch accounts.
 *
 * The invite metadata (team, inviter, role, expiry, optional project hint)
 * is fetched from `GET /api/teams/invites/preview?token=...` — a public
 * endpoint added in iter149 for this page.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, Users, ShieldCheck, ArrowRight, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import DotboxLogo from '@/components/common/DotboxLogo';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

const ROLE_LABEL = { owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer' };
// LocalStorage key for post-auth auto-accept — see AuthContext / login flow.
const PENDING_INVITE_KEY = 'dotbox.pendingInviteToken';

const AcceptInvitePage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const token = params.get('token');

  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [accepting, setAccepting] = useState(false);

  const loadPreview = useCallback(async () => {
    if (!token) {
      setPreviewErr('Missing token');
      setLoadingPreview(false);
      return;
    }
    try {
      const r = await api.get('/teams/invites/preview', { params: { token } });
      setPreview(r.data);
    } catch (e) {
      setPreviewErr(e.response?.data?.detail || 'Invalid or expired invite');
    } finally {
      setLoadingPreview(false);
    }
  }, [token]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  // Auto-persist token when unauthenticated so the register/login flow can
  // pick it up and auto-accept on return.
  useEffect(() => {
    if (token && !user && !authLoading) {
      try { localStorage.setItem(PENDING_INVITE_KEY, token); } catch {
        /* localStorage disabled — user will just re-click accept manually */
      }
    }
  }, [token, user, authLoading]);

  const accept = useCallback(async () => {
    if (!token) return;
    setAccepting(true);
    try {
      await api.post('/teams/join', null, { params: { token } });
      try { localStorage.removeItem(PENDING_INVITE_KEY); } catch { /* ignore */ }
      toast.success(`You're in — welcome to ${preview?.team_name || 'the team'}!`);
      navigate('/dashboard');
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || 'Failed to accept invite';
      if (status === 410) {
        setPreviewErr(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setAccepting(false);
    }
  }, [token, preview, navigate]);

  // Auto-accept for logged-in users whose email matches the invite target.
  useEffect(() => {
    if (!user || !preview || accepting) return;
    if (!token || previewErr) return;
    const mine = (user.email || '').toLowerCase();
    if (mine && preview.email && mine === preview.email.toLowerCase()) {
      accept();
    }
  }, [user, preview, accepting, token, previewErr, accept]);

  // --- render states ---

  return (
    <div className="min-h-screen bg-[#0A1024] text-slate-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <Link to="/" data-testid="invite-nav-home"><DotboxLogo variant="dark" /></Link>
        </div>

        <Card>
          <CardContent className="p-6">
            {loadingPreview ? (
              <div className="py-10 flex flex-col items-center gap-3" data-testid="invite-loading">
                <Loader2 className="h-6 w-6 animate-spin text-red-400" />
                <p className="text-sm text-slate-400">Checking invite…</p>
              </div>
            ) : previewErr ? (
              <div className="py-6 text-center" data-testid="invite-error">
                <div className="h-10 w-10 rounded-full bg-rose-950/40 border border-rose-800/50 mx-auto flex items-center justify-center mb-3">
                  <X className="h-5 w-5 text-rose-300" />
                </div>
                <h2 className="text-lg font-semibold text-slate-100 mb-1">Invite unavailable</h2>
                <p className="text-sm text-slate-400 mb-4">{previewErr}</p>
                <Button asChild className="bg-[#DC2626] hover:bg-[#B91C1C] text-white">
                  <Link to="/">Back to home</Link>
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="h-12 w-12 rounded-full bg-red-600/10 border border-red-600/40 mx-auto flex items-center justify-center mb-3">
                    <Users className="h-6 w-6 text-red-400" />
                  </div>
                  <h1 className="text-xl font-semibold text-slate-100" data-testid="invite-title">
                    You&apos;re invited to
                    <span className="text-red-400"> {preview.team_name}</span>
                  </h1>
                  <p className="text-sm text-slate-400 mt-1">
                    {(preview.inviter?.name || preview.inviter?.email || 'A teammate')} invited you as a{' '}
                    <Badge variant="outline" className="border-sky-800/60 text-sky-300 bg-sky-950/30 text-[10px] px-1.5">
                      {ROLE_LABEL[preview.role] || preview.role}
                    </Badge>
                  </p>
                </div>

                {preview.project_hint && (
                  <div className="rounded-md border border-emerald-800/50 bg-emerald-950/20 p-3 mb-4" data-testid="invite-project-hint">
                    <div className="flex items-start gap-2">
                      <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-300" />
                      <div className="text-xs text-emerald-100">
                        You&apos;ll also gain access to project{' '}
                        <span className="font-semibold text-emerald-50">
                          &ldquo;{preview.project_hint.name}&rdquo;
                        </span>{' '}
                        as a <span className="capitalize">{preview.project_hint.role}</span>.
                      </div>
                    </div>
                  </div>
                )}

                {/* State: not logged in */}
                {!user ? (
                  <div className="space-y-2" data-testid="invite-unauth-actions">
                    <p className="text-xs text-slate-500 text-center mb-2">
                      Sign in with <span className="text-slate-300">{preview.email}</span>, or create an account to accept.
                    </p>
                    <Button asChild className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="invite-register-btn">
                      <Link to={`/register?email=${encodeURIComponent(preview.email)}&invite=${token}`}>
                        Create account & join <ArrowRight className="h-3.5 w-3.5 ml-1" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="w-full border-[#1F2A4D] text-slate-200" data-testid="invite-signin-btn">
                      <Link to={`/login?email=${encodeURIComponent(preview.email)}&invite=${token}`}>
                        Sign in
                      </Link>
                    </Button>
                  </div>
                ) : (user.email || '').toLowerCase() !== (preview.email || '').toLowerCase() ? (
                  <div className="rounded-md border border-amber-800/40 bg-amber-950/20 p-3 text-xs text-amber-200" data-testid="invite-wrong-account">
                    You&apos;re signed in as <strong>{user.email}</strong>, but this invite is for{' '}
                    <strong>{preview.email}</strong>. Please sign out and sign in as the invited email.
                  </div>
                ) : (
                  <Button
                    onClick={accept}
                    disabled={accepting}
                    className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                    data-testid="invite-accept-btn"
                  >
                    {accepting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                    Accept invite
                  </Button>
                )}

                {preview.expires_at && (
                  <p className="text-[10px] text-slate-500 text-center mt-3">
                    Expires {new Date(preview.expires_at).toLocaleDateString()}
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
